import { api } from './client';

/** A catalog product as returned by GET /api/products. */
export interface Product {
  id: string;
  kinguinId: number;
  name: string;
  platform: string;
  genres: string[];
  ageRating: string | null;
  ourPriceCents: number;
  offerCount: number;
  totalQty: number;
  textQty: number;
  isPreorder: boolean;
  imageUrl: string | null;
  coverUrl: string | null;
  available: boolean;
  isDlc: boolean;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  offset: number;
  limit: number;
}

/** Featured items carry a description but omit some list-only fields. */
export interface FeaturedProduct {
  id: string;
  name: string;
  description?: string;
  platform: string;
  coverUrl: string | null;
  imageUrl: string | null;
  ourPriceCents: number;
  isDlc?: boolean;
}

/** Full product detail from GET /api/products/:id. */
export interface ProductDetail extends Product {
  productId: string;
  originalName: string;
  description: string;
  regionalLimitation: string;
  regions: string[];
  languages: string[];
  developers: string[];
  publishers: string[];
  releaseDate: string | null;
  metacriticScore: number | null;
  priceCents: number; // MSRP (strike-through reference)
  qty: number;
  salesCount: number;
  screenshots: string[];
  systemRequirements: { system: string; requirement: string[] }[];
  isAdult: boolean;
}

export interface ProductListParams {
  limit?: number;
  offset?: number;
  sort?: 'price_asc' | 'price_desc' | 'newest' | 'name_asc' | 'popular';
  search?: string;
  platform?: string;
  genre?: string;
  excludeAdult?: boolean;
}

export async function listProducts(params: ProductListParams = {}): Promise<ProductListResponse> {
  const { data } = await api.get<ProductListResponse>('/products', { params });
  return data;
}

export async function getFeatured(): Promise<{ items: FeaturedProduct[] }> {
  const { data } = await api.get<{ items: FeaturedProduct[] }>('/products/featured');
  return data;
}

export async function getProduct(id: string): Promise<ProductDetail> {
  const { data } = await api.get<ProductDetail>(`/products/${id}`);
  return data;
}

/** Cents → "€31.04". */
export function formatPrice(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}
